import { z } from '@customerio/hydra-validation';
import { hydraUiApi } from '@/services/api/hydra-ui-api';
import { getAnalyticsMetaProperties } from './meta-properties';
import { sanitizedLocation } from './sanitized-location';

/** Downstream dashboards key on this exact name. */
export const SIGNUP_COMPLETED_EVENT = 'Signup: Completed';

/**
 * What the signup funnel hands over when a register succeeds, captured
 * before the session flip unmounts the funnel and its form state.
 */
export type SignupCompletion = {
  /** The new account's id — `Signup: Completed` posts to the account-scoped track endpoint. */
  accountId: string;
  signupType: string;
  isGoogleSignup: boolean | undefined;
  isStartup: boolean;
};

/**
 * A completion plus the landing attribution, snapshotted since a reload
 * would lose the module-memory capture.
 */
export type SignupCompletedEvent = SignupCompletion & {
  attribution: Record<string, string>;
};

export function signupCompletedProps(args: {
  signupType: string | undefined;
  isGoogleSignup?: boolean;
  isStartup?: boolean;
}) {
  return {
    'Signup Type': args.signupType,
    'Is Google Signup': args.isGoogleSignup,
    'Is Startup': args.isStartup,
  };
}

/**
 * Fire `Signup: Completed` to the account-scoped track endpoint (no `anon_id`).
 * Best-effort: a failed post must never block the redirect into the app.
 */
export function trackSignupCompletedEvent(event: SignupCompletedEvent): void {
  void hydraUiApi.tracking
    .createTrackEventForAccount({
      params: { path: { account_id: event.accountId } },
      body: {
        event: SIGNUP_COMPLETED_EVENT,
        properties: {
          ...event.attribution,
          ...signupCompletedProps({
            signupType: event.signupType,
            isGoogleSignup: event.isGoogleSignup,
            isStartup: event.isStartup,
          }),
          ...getAnalyticsMetaProperties(),
          ...sanitizedLocation(),
        },
      },
    })
    .catch(() => {});
}

const PENDING_KEY = 'cio-pending-signup-completion';

const pendingListeners = new Set<() => void>();

function notifyPendingListeners(): void {
  for (const listener of pendingListeners) {
    listener();
  }
}

/** `useSyncExternalStore` subscription: fires on every store/take in this tab. */
export function subscribePendingSignupCompletion(listener: () => void): () => void {
  pendingListeners.add(listener);
  return () => {
    pendingListeners.delete(listener);
  };
}

export function hasPendingSignupCompletion(): boolean {
  try {
    return sessionStorage.getItem(PENDING_KEY) != null;
  } catch {
    return false;
  }
}

/**
 * Parks the event for the root observer to take and fire on readiness.
 * Session storage: survives a mid-wizard reload, dies with the tab.
 */
export function storePendingSignupCompletion(event: SignupCompletedEvent): void {
  try {
    sessionStorage.setItem(PENDING_KEY, JSON.stringify(event));
  } catch {
    // Storage unavailable: the event is lost, the redirect is not.
  }
  notifyPendingListeners();
}

const pendingSignupCompletionSchema = z.object({
  accountId: z.string(),
  signupType: z.string(),
  isGoogleSignup: z.boolean().optional(),
  isStartup: z.boolean(),
  attribution: z.record(z.string(), z.string()),
});

/** Read and clear the parked event, or `null` when there is none (or it is unreadable). */
export function takePendingSignupCompletion(): SignupCompletedEvent | null {
  try {
    const raw = sessionStorage.getItem(PENDING_KEY);
    if (raw == null) {
      return null;
    }
    sessionStorage.removeItem(PENDING_KEY);
    notifyPendingListeners();
    const json: unknown = JSON.parse(raw);
    const result = pendingSignupCompletionSchema.safeParse(json);
    // JSON drops an `undefined` field, so the parsed shape restores it explicitly.
    return result.success ? { ...result.data, isGoogleSignup: result.data.isGoogleSignup } : null;
  } catch {
    return null;
  }
}
